import { Button, VStack } from '@chakra-ui/react';
import React from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';

import type { SmartContractSecurityAuditSubmission } from 'types/api/contract';

import type { ResourceError } from 'lib/api/resources';
import useApiFetch from 'lib/api/useApiFetch';
import dayjs from 'lib/date/dayjs';
import useToast from 'lib/hooks/useToast';
import { useI18n } from 'lib/i18n/context';
import FormFieldCheckbox from 'ui/shared/forms/fields/FormFieldCheckbox';
import FormFieldEmail from 'ui/shared/forms/fields/FormFieldEmail';
import FormFieldText from 'ui/shared/forms/fields/FormFieldText';
import FormFieldUrl from 'ui/shared/forms/fields/FormFieldUrl';

import { ADDRESS_CONTRACT_KEYS } from '../../i18n';

interface Props {
  address?: string;
  onSuccess: () => void;
}

export type Inputs = {
  submitter_name: string;
  submitter_email: string;
  is_project_owner: boolean;
  project_name: string;
  project_url: string;
  audit_company_name: string;
  audit_report_url: string;
  audit_publish_date: string;
  comment?: string;
};

type AuditSubmissionErrors = {
  errors: Record<keyof Inputs, Array<string>>;
};

const ContractSubmitAuditForm = ({ address, onSuccess }: Props) => {
  const { t } = useI18n();
  const containerRef = React.useRef<HTMLFormElement>(null);

  const apiFetch = useApiFetch();
  const toast = useToast();

  const formApi = useForm<Inputs>({
    mode: 'onTouched',
    defaultValues: { is_project_owner: false },
  });
  const { handleSubmit, formState, setError } = formApi;

  const onFormSubmit: SubmitHandler<Inputs> = React.useCallback(async(data) => {
    try {
      await apiFetch<'contract_security_audits', SmartContractSecurityAuditSubmission, AuditSubmissionErrors>('contract_security_audits', {
        pathParams: { hash: address },
        fetchParams: {
          method: 'POST',
          body: data,
        },
      });

      toast({
        position: 'top-right',
        title: t(ADDRESS_CONTRACT_KEYS.audit.successTitle),
        description: t(ADDRESS_CONTRACT_KEYS.audit.successDescription),
        status: 'success',
        variant: 'subtle',
        isClosable: true,
      });

      onSuccess();

    } catch (_error) {
      const error = _error as ResourceError<AuditSubmissionErrors>;
      // add scroll to the error field
      const errorMap = error?.payload?.errors;
      if (errorMap && Object.keys(errorMap).length) {
        (Object.keys(errorMap) as Array<keyof Inputs>).forEach((errorField) => {
          setError(errorField, { type: 'custom', message: errorMap[errorField].join(', ') });
        });
      } else {
        toast({
          position: 'top-right',
          title: t(ADDRESS_CONTRACT_KEYS.audit.errorTitle),
          description: (_error as ResourceError<{ message: string }>)?.payload?.message || t(ADDRESS_CONTRACT_KEYS.audit.genericError),
          status: 'error',
          variant: 'subtle',
          isClosable: true,
        });
      }
    }
  }, [ apiFetch, address, toast, setError, onSuccess, t ]);

  return (
    <FormProvider { ...formApi }>
      <form noValidate onSubmit={ handleSubmit(onFormSubmit) } autoComplete="off" ref={ containerRef }>
        <VStack gap={ 5 } alignItems="flex-start">
          <FormFieldText<Inputs> name="submitter_name" isRequired placeholder={ t(ADDRESS_CONTRACT_KEYS.audit.submitterName) }/>
          <FormFieldEmail<Inputs> name="submitter_email" isRequired placeholder={ t(ADDRESS_CONTRACT_KEYS.audit.submitterEmail) }/>
          <FormFieldCheckbox<Inputs, 'is_project_owner'>
            name="is_project_owner"
            label={ t(ADDRESS_CONTRACT_KEYS.audit.ownerCheckbox) }
          />
          <FormFieldText<Inputs> name="project_name" isRequired placeholder={ t(ADDRESS_CONTRACT_KEYS.audit.projectName) }/>
          <FormFieldUrl<Inputs> name="project_url" isRequired placeholder={ t(ADDRESS_CONTRACT_KEYS.audit.projectUrl) }/>
          <FormFieldText<Inputs> name="audit_company_name" isRequired placeholder={ t(ADDRESS_CONTRACT_KEYS.audit.auditCompanyName) }/>
          <FormFieldUrl<Inputs> name="audit_report_url" isRequired placeholder={ t(ADDRESS_CONTRACT_KEYS.audit.auditReportUrl) }/>
          <FormFieldText<Inputs>
            name="audit_publish_date"
            type="date"
            max={ dayjs().format('YYYY-MM-DD') }
            isRequired
            placeholder={ t(ADDRESS_CONTRACT_KEYS.audit.auditPublishDate) }
          />
          <FormFieldText<Inputs>
            name="comment"
            placeholder={ t(ADDRESS_CONTRACT_KEYS.audit.comment) }
            maxH="160px"
            rules={{ maxLength: 300 }}
            asComponent="Textarea"
          />
        </VStack>
        <Button
          type="submit"
          size="lg"
          mt={ 8 }
          isLoading={ formState.isSubmitting }
          loadingText={ t(ADDRESS_CONTRACT_KEYS.actions.sendRequest) }
          isDisabled={ !formState.isDirty }
        >
          { t(ADDRESS_CONTRACT_KEYS.actions.sendRequest) }
        </Button>
      </form>
    </FormProvider>
  );
};

export default React.memo(ContractSubmitAuditForm);
